import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import {
  RefundVm,
  RefundListResponse,
  RefundListItem,
  RefundVendor,
  RefundBill,
  RefundBillLine,
  ApiResponse,
} from '@/api/types'

/**
 * Vendor Refunds (Credit Notes) service
 */
export class RefundsService {
  /**
   * Get paginated list of refunds for a branch
   */
  async getRefunds(
    branchId: number,
    pageNumber: number,
    pageSize: number
  ): Promise<RefundListResponse> {
    try {
      const url = endpoints.refunds.getAll(branchId, pageNumber, pageSize)
      console.log('🔵 [RefundsService.getRefunds] Calling:', url, { branchId, pageNumber, pageSize })
      const response = await apiClient.get<ApiResponse<RefundListResponse> | RefundListResponse | any>(url)
      console.log('🔵 [RefundsService.getRefunds] Full Response:', response)
      console.log('🔵 [RefundsService.getRefunds] Response Data:', response.data)
      
      const payload = response.data

      if (payload && typeof payload === 'object') {
        if ('success' in payload && 'data' in payload) {
          console.log('🔵 [RefundsService.getRefunds] Returning from success.data')
          return payload.data as RefundListResponse
        }

        if ('data' in payload && Array.isArray(payload.data)) {
          console.log('🔵 [RefundsService.getRefunds] Returning from data property:', payload.data.length, 'items')
          console.log('🔵 [RefundsService.getRefunds] Sample item:', payload.data[0])
          return {
            totalRecords: payload.totalRecords ?? payload.data.length,
            pageNumber: payload.pageNumber ?? pageNumber,
            pageSize: payload.pageSize ?? pageSize,
            data: payload.data as RefundListItem[],
          }
        }
      }

      if (Array.isArray(payload)) {
        console.log('🔵 [RefundsService.getRefunds] Response is direct array:', payload.length, 'items')
        return {
          totalRecords: payload.length,
          pageNumber,
          pageSize,
          data: payload as RefundListItem[],
        }
      }

      console.warn('⚠️ [RefundsService.getRefunds] Unexpected response format:', payload)
      return {
        totalRecords: payload?.totalRecords ?? 0,
        pageNumber: payload?.pageNumber ?? pageNumber,
        pageSize: payload?.pageSize ?? pageSize,
        data: payload?.data ?? [],
      }
    } catch (error: any) {
      console.error('❌ [RefundsService.getRefunds] Error:', error)
      console.error('❌ [RefundsService.getRefunds] Error Response:', error.response)
      console.error('❌ [RefundsService.getRefunds] Error Data:', error.response?.data)
      throw error
    }
  }

  /**
   * Get all vendors for refunds
   */
  async getAllVendors(branchId: number): Promise<RefundVendor[]> {
    const url = endpoints.refunds.getAllVendors(branchId)
    const response = await apiClient.get<ApiResponse<RefundVendor[]> | RefundVendor[]>(url)
    
    if (Array.isArray(response.data)) {
      return response.data
    }
    
    if (response.data && 'data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    
    return []
  }

  /**
   * Get all bills for refunds (with remaining amount > 0)
   */
  async getAllBills(branchId: number): Promise<RefundBill[]> {
    const url = endpoints.refunds.getAllBills(branchId)
    const response = await apiClient.get<ApiResponse<RefundBill[]> | RefundBill[]>(url)
    
    if (Array.isArray(response.data)) {
      return response.data
    }
    
    if (response.data && 'data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    
    return []
  }

  /**
   * Get bill lines for a specific bill
   * API returns: { bill: {...}, billsLines: [...] }
   */
  async getBillLines(branchId: number, billId: number): Promise<{ bill: any; billsLines: RefundBillLine[] }> {
    try {
      const url = endpoints.refunds.getBillLines(branchId, billId)
      console.log('🔵 [RefundsService.getBillLines] Calling:', url, 'billId:', billId)
      const response = await apiClient.get<any>(url)
      console.log('🔵 [RefundsService.getBillLines] Full Response:', response)
      console.log('🔵 [RefundsService.getBillLines] Response Data:', response.data)
      
      const responseData = response.data
      
      // Handle response structure: { bill: {...}, billsLines: [...] }
      if (responseData && typeof responseData === 'object') {
        // Check if data is wrapped in 'data' property
        let data = responseData
        if ('data' in responseData && typeof responseData.data === 'object') {
          data = responseData.data
        }
        
        if ('billsLines' in data && Array.isArray(data.billsLines)) {
          console.log('🔵 [RefundsService.getBillLines] Found billsLines array:', data.billsLines.length, 'items')
          return {
            bill: data.bill || {},
            billsLines: data.billsLines,
          }
        }
        
        // Also check for 'billLines' (without 's')
        if ('billLines' in data && Array.isArray(data.billLines)) {
          console.log('🔵 [RefundsService.getBillLines] Found billLines array:', data.billLines.length, 'items')
          return {
            bill: data.bill || {},
            billsLines: data.billLines,
          }
        }
      }
      
      // Fallback: check if response.data is directly an array
      if (Array.isArray(responseData)) {
        console.log('🔵 [RefundsService.getBillLines] Response is direct array:', responseData.length, 'items')
        return {
          bill: {},
          billsLines: responseData,
        }
      }
      
      console.warn('⚠️ [RefundsService.getBillLines] Unexpected response format:', responseData)
      return { bill: {}, billsLines: [] }
    } catch (error: any) {
      console.error('❌ [RefundsService.getBillLines] Error:', error)
      console.error('❌ [RefundsService.getBillLines] Error Response:', error.response)
      console.error('❌ [RefundsService.getBillLines] Error Data:', error.response?.data)
      throw error
    }
  }

  /**
   * Save new refund
   */
  async saveRefund(refund: RefundVm): Promise<any> {
    const url = endpoints.refunds.insert
    const response = await apiClient.post<ApiResponse<any> | any>(url, refund)
    return response.data
  }

  /**
   * Update existing refund
   */
  async updateRefund(refund: RefundVm): Promise<any> {
    const url = endpoints.refunds.update
    const response = await apiClient.put<ApiResponse<any> | any>(url, refund)
    return response.data
  }
}

export const refundsService = new RefundsService()

